import React, { useEffect, useRef } from "react";

import styles from "./Toast.scss";

export interface ToastOverlayProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Optional CSS value for the top position of the toast overlay.
   *
   * Use this to position toasts below fixed headers or other UI elements that might
   * obscure them. Accepts any valid CSS top value (e.g., "80px", "5rem", "10%").
   *
   * @default undefined (uses the default top position from Toast.scss)
   */
  top?: string;
}

export function ToastOverlay({
  top,
  style,
  children,
  ...props
}: ToastOverlayProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const previousToastCount = useRef(0);
  const overlayStyle = top ? { ...style, top } : style;
  const toastKeys = React.Children.toArray(children)
    .map((child) => (React.isValidElement(child) ? String(child.key) : ""))
    .join("|");
  const toastCount = React.Children.count(children);

  useEffect(() => {
    const added = toastCount > previousToastCount.current;
    previousToastCount.current = toastCount;
    if (added) {
      raiseToTopLayer(overlayRef.current);
    } else if (toastCount === 0) {
      lowerFromTopLayer(overlayRef.current);
    }
  }, [toastKeys, toastCount]);

  return (
    <div
      {...props}
      ref={overlayRef}
      id="toast-overlay"
      data-testid="toast-overlay"
      className={styles.toastOverlay}
      style={overlayStyle}
    >
      {children}
    </div>
  );
}

function raiseToTopLayer(overlay: HTMLDivElement | null): void {
  if (!overlay || !supportsPopover(overlay)) {
    return;
  }
  overlay.setAttribute("popover", "manual");
  if (overlay.matches(":popover-open")) {
    overlay.hidePopover();
  }
  overlay.showPopover();
}

function lowerFromTopLayer(overlay: HTMLDivElement | null): void {
  if (!overlay || !supportsPopover(overlay)) {
    return;
  }
  if (overlay.matches(":popover-open")) {
    overlay.hidePopover();
  }
}

function supportsPopover(element: HTMLElement): boolean {
  return (
    typeof element.showPopover === "function" &&
    typeof element.hidePopover === "function"
  );
}
